import { EditorTable, TableShape } from 'components/tableMaps/CreateTableMapWizard/context';
import { MapFeature } from 'components/tableMaps/CreateTableMapWizard/features';
import { MapOpening } from 'components/tableMaps/CreateTableMapWizard/openings';
import {
    AreaShapeKind,
    EditorArea,
    Point,
    SizeMeters,
    TABLE_SHAPE_DEFAULTS,
    TABLE_SHAPES,
    TableShapeSizes,
} from 'components/tableMaps/CreateTableMapWizard/mapGeometry';
import { TableMapDetails, TableMapListItem } from './reducer';

export interface MapAreaShapeDTO {
    kind: AreaShapeKind;
    offset: Point;
    size: SizeMeters;
    points: Point[] | null;
}

export interface MapTableDTO {
    id: string;
    mapId: string;
    name: string;
    updateDate: string | null;
    publishedTimestamp: number;
    totalSeats: number;
    shape: 'Round' | 'Square' | 'Rectangle';
    position: { x: number; y: number };
    rotation?: number;
    width: number;
    height: number;
    isActive: boolean;
    isPositionLocked: boolean;
    qrCode: string | null;
}

export interface TablesMapDTO {
    id: string;
    name: string;
    updateDate: string | null;
    isActive: boolean;
    isDraft: boolean;
    draftStep: number | null;
    entrances: MapOpening[];
    features: MapFeature[];
    size: SizeMeters;
    area: MapAreaShapeDTO;
    tables: MapTableDTO[];
}

export interface TablesMapModel {
    name: string;
    isActive: boolean;
    /** The backend treats an omitted flag as false, i.e. it publishes — always send it. */
    isDraft: boolean;
    draftStep: number | null;
    /** The backend replaces stored `entrances` and `features` — always send back what was read. */
    entrances: MapOpening[];
    features: MapFeature[];
    size: SizeMeters;
    area: MapAreaShapeDTO;
}

export interface MapTableModel {
    name: string;
    totalSeats: number;
    shape: 'Round' | 'Square' | 'Rectangle';
    position: { x: number; y: number };
    rotation: number;
    width: number;
    height: number;
    isActive: boolean;
    isPositionLocked: boolean;
    qrCode: string | null;
}

export interface QRCodeDTO {
    id: string;
    name: string;
    service: string;
    url: string;
    area: string;
    isUsed: boolean;
}

const SHAPE_TO_BACKEND: Record<TableShape, 'Round' | 'Square' | 'Rectangle'> = {
    round: 'Round',
    square: 'Square',
    rectangle: 'Rectangle',
};

const SHAPE_FROM_BACKEND: Record<'Round' | 'Square' | 'Rectangle', TableShape> = {
    Round: 'round',
    Square: 'square',
    Rectangle: 'rectangle',
};

export function deriveMapStatus(dto: TablesMapDTO): TableMapListItem['status'] {
    if (dto.isDraft) return 'Draft';
    return dto.isActive ? 'Active' : 'Inactive';
}

/** Latest change of the map itself or of any of its tables. */
export function lastUpdatedIso(dto: TablesMapDTO): string | null {
    const stamps = [dto.updateDate, ...(dto.tables ?? []).map((t) => t.updateDate)]
        .filter((d): d is string => !!d)
        .map((d) => new Date(d).getTime())
        .filter((ms) => !Number.isNaN(ms));

    if (stamps.length === 0) return null;
    return new Date(Math.max(...stamps)).toISOString();
}

export function areaDtoToEditorArea(dto: MapAreaShapeDTO): EditorArea {
    return {
        kind: dto.kind,
        offset: { x: dto.offset.x, y: dto.offset.y },
        size: { width: dto.size.width, height: dto.size.height },
        points: dto.points ? dto.points.map((p) => ({ x: p.x, y: p.y })) : null,
    };
}

export function editorAreaToAreaModel(area: EditorArea): MapAreaShapeDTO {
    return {
        kind: area.kind,
        offset: { x: area.offset.x, y: area.offset.y },
        size: { width: area.size.width, height: area.size.height },
        points: area.points,
    };
}

export function mapTableDtoToEditorTable(dto: MapTableDTO): EditorTable {
    return {
        id: dto.id,
        label: dto.name,
        shape: SHAPE_FROM_BACKEND[dto.shape],
        seats: dto.totalSeats,
        x: dto.position.x,
        y: dto.position.y,
        width: dto.width || 0,
        height: dto.height || 0,
        rotation: dto.rotation ?? 0,
        qrCode: dto.qrCode || null,
        isActive: dto.isActive && !!dto.qrCode,
        isPositionLocked: dto.isPositionLocked,
    };
}

export function editorTableToMapTableModel(t: EditorTable): MapTableModel {
    return {
        name: t.label,
        totalSeats: t.seats,
        shape: SHAPE_TO_BACKEND[t.shape],
        position: { x: t.x, y: t.y },
        rotation: t.rotation,
        width: t.width,
        height: t.height,
        isActive: t.isActive && !!t.qrCode,
        isPositionLocked: t.isPositionLocked,
        qrCode: t.qrCode || null,
    };
}

export function tablesMapDtoToListItem(dto: TablesMapDTO): TableMapListItem {
    const tables = dto.tables ?? [];
    return {
        id: dto.id,
        name: dto.name,
        tablesCount: tables.length,
        totalSeats: tables.reduce((sum, t) => sum + t.totalSeats, 0),
        status: deriveMapStatus(dto),
        updatedAt: lastUpdatedIso(dto),
    };
}

/** Per-shape defaults are never persisted, so they are read back off the saved tables. */
function seedTableDefaults(tables: EditorTable[]): TableShapeSizes {
    const seeded = { ...TABLE_SHAPE_DEFAULTS };
    for (const shape of TABLE_SHAPES) {
        const sized = tables.find((t) => t.shape === shape && t.width > 0 && t.height > 0);
        if (sized) {
            seeded[shape] = { width: sized.width, height: sized.height };
        }
    }
    return seeded;
}

export function tablesMapDtoToDetails(dto: TablesMapDTO): TableMapDetails {
    const dtoTables = dto.tables ?? [];
    const tables = dtoTables.map(mapTableDtoToEditorTable);
    return {
        id: dto.id,
        name: dto.name,
        isActive: dto.isActive,
        isDraft: dto.isDraft,
        draftStep: dto.draftStep,
        gridEnabled: true,
        entrances: dto.entrances,
        features: dto.features ?? [],
        size: { width: dto.size.width, height: dto.size.height },
        area: areaDtoToEditorArea(dto.area),
        tableDefaults: seedTableDefaults(tables),
        tables,
        publishedAt: lastUpdatedIso(dto),
    };
}
